import React, { useEffect, useState } from 'react';

import { useMedia } from 'react-use';
import api from '../../../lib/api';
import { showToast } from '../../shared/Toast';
import { fromRole, isXDaysOlder, LOCAL_STORAGE_OTP_RECIPIENTS, ProjectModel, SbCurrentUser, SbRecipientModel, SubmissionBoardModel } from '../component/SbModel';
import {
  url_api_v1_submission_board_otp_get,
  url_api_v1_submission_board_otp_post,
} from '../../../lib/apiUrl';
import OTPInput from '../component/OtpInput';
import SbDetailsRecipient from './SbDetailsRecipient';
import logo from '../../../assets/images/filmik-horizontal-logo-black.svg'; 
import { CircleXIcon } from 'lucide-react';

export interface VerifyOtpProps {
  method: 'sms' | 'email';
  contactInfo: string;
  onSuccess: (completeOtp: string) => void;
  onResend: () => void;
  onChangeMethod?: () => void;
  createdAt?: string; // ISO string for when the code was created
}

// for recipient : director
// For SB details for recipient, we dont show all parameters like SbDetails.
// from SB controller: show method
export default function VerifyOtp({
  method,
  contactInfo,
  onSuccess,
  onResend,
  onChangeMethod,
  createdAt
}: VerifyOtpProps) {
  const OTP_LENGTH = 6;
  const RESEND_TIME_SECONDS = 180; // 3 minutes

  const [otpValues, setOtpValues] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [completeOtp, setCompleteOtp] = useState<string | null>(null);
  const [timer, setTimer] = useState<number>(RESEND_TIME_SECONDS);
  const [resendEnabled, setResendEnabled] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Timer logic with useEffect
  useEffect(() => {
    if (resendEnabled < 0) {
      return;
    }
      const countdown = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(countdown);
            setResendEnabled(1);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);

    // Cleanup function
    return () => clearInterval(countdown);
  }, [resendEnabled]); // Dependency to restart timer when resend is enabled/disabled

  // Format the timer into MM:SS format
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  // Mask contact info
  const getMaskedContact = () => {
    if (!contactInfo) return '';
    
    if (method === 'sms') {
      // Mask phone number: show only last 4 digits
      if (contactInfo.length <= 4) return contactInfo;
      return '*'.repeat(contactInfo.length - 4) + contactInfo.slice(-4);
    } else {
      // Mask email: show first and last char of local part
      const [local, domain] = contactInfo.split('@');
      if (!domain) return contactInfo; // Invalid email format
      if (local.length <= 2) return contactInfo;
      const maskedLocal = `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}`;
      return `${maskedLocal}@${domain}`;
    }
  };


  const handleOtpChange = (newOtp: string[]) => {
    setOtpValues(newOtp);
    const otpString = newOtp.join('');
    if (otpString.length === OTP_LENGTH) {
      setCompleteOtp(otpString);
    } else {
      setCompleteOtp(null);
    }
  };

  const performSubmit = async (otp: string) => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await onSuccess(otp);
    } catch (error) {
      setErrorMessage("Invalid or expired verification code. Please try again.");
      showToast('Verification failed. Please check the code and try again.', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (completeOtp) {
      await performSubmit(completeOtp);
    }
  };

  const handleResendClick = async () => {
    console.log('Resending OTP...');
    setTimer(RESEND_TIME_SECONDS);
    setResendEnabled(0);
    setOtpValues(new Array(OTP_LENGTH).fill('')); // Reset the OTP inputs
    setCompleteOtp(null);
    onResend();
  };

  return (
    <div className="w-full mx-auto  bg-gray-100 min-h-screen">
      
        <>
          <div className="w-full flex flex-col ">
            <div className="w-full flex-1">
              <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-6 sm:px-0">
                <div className="flex flex-col py-10 px-14 gap-2-1 bg-white rounded-lg shadow-xl w-full max-w-md"
                style={{
                  boxShadow:'0px 2px 16px 0px #00000040'
                }}>
                  <div className="flex justify-center mb-6">
                    <img src={logo} alt="Filmik Logo" className="h-8"/>
                  </div>
        
                  <h1 className="text-2xl font-semibold text-center text-gray-800 mb-6">
                    Verify Your Account
                  </h1>
                   {errorMessage.length > 0 && (
                    <div className="flex flex-row mt-4 items-center justify-center">
                      <div className="flex flex-col sm:flex-row px-2-1 py-2-1 gap-1 rounded-md bg-red-701 border-red-450 border text-xs text-red-601 justify-center items-center text-center break-words">
                        <CircleXIcon className="w-6 h-6" />
                        <div className="font-medium font-xs">{errorMessage}</div>
                      </div>
                    </div>)}
                  <p className="text-center font-medium mb-6">
                    For your security, we need to verify your identity.
                  </p>
                  <p className="text-center text-sm text-gray-500 mb-6">
                    We’ve sent a 6-digit code to {getMaskedContact()}
                  </p>
                  <form onSubmit={handleSubmit}>
                    <OTPInput
                      length={OTP_LENGTH}
                      value={otpValues}
                      onChange={handleOtpChange}
                      onComplete={performSubmit}
                      disabled={isSubmitting}
                    />

                    <div className="flex justify-center items-center mt-6">
                      <span className="text-center text-sm text-gray-500 mb-6">
                        Code expires in <span className="font-semibold">{formatTime(timer)}</span>
                      </span>

                    </div>

                    <button
                      type="submit"
                      disabled={!completeOtp || isSubmitting}
                      className={`
              w-full mt-8 py-3 rounded-lg font-semibold text-white
              ${
                completeOtp && !isSubmitting
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-400 cursor-not-allowed'
              }
              transition-colors duration-200
            `}
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify Code'}
                    </button>
                     <div className="flex justify-center items-center mt-6">
                     
                      {resendEnabled == 1 ? (
                        <div
                          onClick={handleResendClick}
                          className="text-blue-600 font-semibold cursor-pointer text-sm hover:underline"
                        >
                          Resend OTP
                        </div>
                      ) : (
                        <div
                          className="text-gray-400 font-semibold text-sm cursor-not-allowed"
                        >
                          Resend OTP
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      
    </div>
  );
}
